/**
 * Bulk task input parser.
 *
 * Input format:
 *   - One task per non-empty line.
 *   - Lines starting with "# " are section headers; subsequent task lines
 *     belong to that section's project.
 *   - Lines before the first header belong to the "default" section (the
 *     default project chosen in the UI).
 *
 * Each task line is run through the existing single-task NLP parser so
 * priority / duration / deadline / energy work exactly as in Quick Capture.
 *
 * Each section header is resolved against the project list:
 *   - exact (case-insensitive) match -> use that project
 *   - close match (Levenshtein) -> flag as fuzzy suggestion
 *   - otherwise -> flag as new
 */

import type { Project } from '@trackmind/core';
import { parseTaskInput, type ParsedTask } from './nlpParser';

export type ProjectResolution =
  | { kind: 'default' }
  | { kind: 'existing-exact'; projectId: string }
  | { kind: 'fuzzy-suggestion'; suggestedProjectId: string; rawName: string }
  | { kind: 'new'; rawName: string }
  | { kind: 'user-mapped-existing'; projectId: string; rawName: string }
  | { kind: 'user-confirmed-new'; rawName: string };

export interface BulkTask {
  id: string;
  rawLine: string;
  parsed: ParsedTask;
}

export interface BulkSection {
  id: string;
  rawHeader: string | null; // null for the default section
  resolution: ProjectResolution;
  tasks: BulkTask[];
}

// ─── Fuzzy match ────────────────────────────────────────────────────────────

function levenshtein(a: string, b: string): number {
  const n = a.length;
  const m = b.length;
  if (n === 0) return m;
  if (m === 0) return n;
  const dp: number[] = new Array(m + 1);
  for (let j = 0; j <= m; j++) dp[j] = j;
  for (let i = 1; i <= n; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= m; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : Math.min(prev, dp[j], dp[j - 1]) + 1;
      prev = tmp;
    }
  }
  return dp[m];
}

export function resolveProjectName(rawName: string, projects: Project[]): ProjectResolution {
  const trimmed = rawName.trim();
  if (!trimmed) return { kind: 'new', rawName: trimmed };

  const lowered = trimmed.toLowerCase();

  const exact = projects.find(p => p.name.toLowerCase() === lowered);
  if (exact) return { kind: 'existing-exact', projectId: exact.id };

  // Find closest by Levenshtein distance.
  // Threshold scales with name length: max(2, 30% of length). Short names get
  // a stricter absolute floor; long names get more slack.
  let best: { project: Project; distance: number } | null = null;
  for (const p of projects) {
    if (p.status === 'archived') continue;
    const d = levenshtein(lowered, p.name.toLowerCase());
    if (!best || d < best.distance) best = { project: p, distance: d };
  }
  const threshold = Math.max(2, Math.floor(trimmed.length * 0.3));
  if (best && best.distance > 0 && best.distance <= threshold) {
    return { kind: 'fuzzy-suggestion', suggestedProjectId: best.project.id, rawName: trimmed };
  }

  return { kind: 'new', rawName: trimmed };
}

// ─── Parser ─────────────────────────────────────────────────────────────────

export interface BulkParseInput {
  text: string;
  projects: Project[];
}

export function parseBulkInput({ text, projects }: BulkParseInput): BulkSection[] {
  const sections: BulkSection[] = [];
  let sectionCounter = 0;
  let taskCounter = 0;

  // Start with an empty default section. We'll drop it at the end if no
  // task lines were typed before the first header.
  let current: BulkSection = {
    id: `s${sectionCounter++}`,
    rawHeader: null,
    resolution: { kind: 'default' },
    tasks: [],
  };

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    // Header: a line starting with "#", optionally followed by space, then
    // the project name. Both "# Foo" and "#Foo" work. The space is friendlier
    // for the eye but most people just type "#Foo" out of habit.
    const headerMatch = line.match(/^#\s*(.+)$/);
    if (headerMatch) {
      // Push the section we were building, even empty default if the user
      // explicitly started with a header — we'll filter empties at the end.
      sections.push(current);
      const rawName = headerMatch[1].trim();
      current = {
        id: `s${sectionCounter++}`,
        rawHeader: rawName,
        resolution: resolveProjectName(rawName, projects),
        tasks: [],
      };
      continue;
    }

    const parsed = parseTaskInput(line);
    current.tasks.push({ id: `t${taskCounter++}`, rawLine: line, parsed });
  }

  sections.push(current);

  // Drop sections with no tasks AND no explicit header (i.e. the empty
  // default section when the user started directly with a header).
  return sections.filter(s => s.tasks.length > 0 || s.rawHeader !== null);
}

// ─── Helpers ────────────────────────────────────────────────────────────────

export function isBulkInput(text: string): boolean {
  if (text.includes('\n')) return true;
  // A single line that LOOKS like a header (with a space — "#hashtag" alone
  // is more likely a tag-style title than a real header) flips into bulk
  // mode so the section UI shows up.
  if (/^#\s/m.test(text)) return true;
  return false;
}

/**
 * Returns true if every section's resolution is "decided" — i.e. it will
 * neither prompt nor surprise the user on submit. Default / existing-exact /
 * user-mapped-existing / user-confirmed-new are all decided. New and
 * fuzzy-suggestion are NOT decided (they need a click to resolve).
 */
export function allResolutionsDecided(sections: BulkSection[]): boolean {
  return sections.every(s => {
    const k = s.resolution.kind;
    return k === 'default' || k === 'existing-exact' || k === 'user-mapped-existing' || k === 'user-confirmed-new';
  });
}

/**
 * Count how many *new* projects will be created on submit, assuming any
 * undecided resolutions are auto-accepted (fuzzy -> use suggestion;
 * new -> create as new). Used by the Stage-1 direct-submit button label
 * so the user sees the side effect before clicking.
 */
export function countNewProjectsOnSubmit(sections: BulkSection[]): number {
  return sections.filter(s => s.resolution.kind === 'new' || s.resolution.kind === 'user-confirmed-new').length;
}

// ─── Row-based representation ───────────────────────────────────────────────
//
// The row-based capture UI works with a flat list of RowItems rather than
// the nested BulkSection[] format. Operations like add/remove/insert/move
// are trivial on a flat list; we only build the BulkSection[] structure
// when handing off to submit or to the Stage 2 review dialog.

export type EnergyLevel = 'low' | 'medium' | 'high';

export interface TaskRowItem {
  id: string;
  kind: 'task';
  title: string;
  deadline: string; // YYYY-MM-DD or ''
  priority: number; // 1-5
  energy: EnergyLevel;
  estimatedMinutes: number;
}

export interface HeaderRowItem {
  id: string;
  kind: 'header';
  headerName: string;
  resolution: ProjectResolution;
}

export type RowItem = TaskRowItem | HeaderRowItem;

// The default values a fresh task row starts with. Used both for new rows
// and for the "is this cell at its default value?" check that decides
// whether NLP parsing should override a cell.
export const ROW_DEFAULTS = {
  deadline: '' as string,
  priority: 3,
  energy: 'medium' as EnergyLevel,
  estimatedMinutes: 30,
};

let _idCounter = 0;
export function nextRowId(prefix = 'r'): string {
  _idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${_idCounter}`;
}

export function makeEmptyTaskRow(): TaskRowItem {
  return {
    id: nextRowId('t'),
    kind: 'task',
    title: '',
    deadline: ROW_DEFAULTS.deadline,
    priority: ROW_DEFAULTS.priority,
    energy: ROW_DEFAULTS.energy,
    estimatedMinutes: ROW_DEFAULTS.estimatedMinutes,
  };
}

export function makeHeaderRow(name: string, projects: Project[]): HeaderRowItem {
  return {
    id: nextRowId('h'),
    kind: 'header',
    headerName: name,
    resolution: resolveProjectName(name, projects),
  };
}

/**
 * Convert a flat row list to the nested BulkSection[] format that the
 * existing submit / Stage 2 review code expects. Empty-title task rows
 * are dropped silently.
 */
export function rowsToSections(rows: RowItem[]): BulkSection[] {
  const sections: BulkSection[] = [];
  let current: BulkSection = {
    id: 'sec-default',
    rawHeader: null,
    resolution: { kind: 'default' },
    tasks: [],
  };

  const flush = () => {
    if (current.tasks.length > 0 || current.rawHeader !== null) sections.push(current);
  };

  for (const row of rows) {
    if (row.kind === 'header') {
      flush();
      current = {
        id: row.id,
        rawHeader: row.headerName,
        resolution: row.resolution,
        tasks: [],
      };
      continue;
    }
    // Task row. Apply NLP parse here too, so users who type
    // "Meeting tomorrow 2h" and click Submit without ever pressing
    // Enter / Tab still get their metadata extracted. The parse only
    // overrides cells still at their default value, so any explicit
    // cell edit the user already made wins.
    const applied = applyParseToRow(row);
    const title = applied.title.trim();
    if (!title) continue;
    current.tasks.push({
      id: applied.id,
      rawLine: row.title,
      parsed: {
        title,
        deadline: applied.deadline ? new Date(applied.deadline + 'T23:59:00') : undefined,
        priority: applied.priority,
        estimatedMinutes: applied.estimatedMinutes,
        energyLevel: applied.energy,
      },
    });
  }
  flush();
  return sections;
}

/**
 * Convert pasted multi-line text into row items. Uses the existing
 * bulk parser to handle "#Header" lines and NLP parse each task line.
 */
export function textToRows(text: string, projects: Project[]): RowItem[] {
  const sections = parseBulkInput({ text, projects });
  const rows: RowItem[] = [];
  for (const s of sections) {
    if (s.rawHeader !== null) {
      rows.push({
        id: nextRowId('h'),
        kind: 'header',
        headerName: s.rawHeader,
        resolution: s.resolution,
      });
    }
    for (const t of s.tasks) {
      const p = t.parsed;
      const deadlineStr = p.deadline
        ? `${p.deadline.getFullYear()}-${String(p.deadline.getMonth() + 1).padStart(2, '0')}-${String(p.deadline.getDate()).padStart(2, '0')}`
        : ROW_DEFAULTS.deadline;
      rows.push({
        id: nextRowId('t'),
        kind: 'task',
        title: p.title || t.rawLine,
        deadline: deadlineStr,
        priority: p.priority ?? ROW_DEFAULTS.priority,
        energy: p.energyLevel ?? ROW_DEFAULTS.energy,
        estimatedMinutes: p.estimatedMinutes ?? ROW_DEFAULTS.estimatedMinutes,
      });
    }
  }
  return rows;
}

/**
 * Apply NLP parse to a single task row's title and selectively update
 * its other cells. The rule: replace title with the cleaned version
 * always; update other cells only when they're at their default value
 * (so manual edits the user already made are preserved).
 */
export function applyParseToRow(row: TaskRowItem): TaskRowItem {
  const p = parseTaskInput(row.title);

  const cleanedTitle = p.title || row.title;

  const next: TaskRowItem = { ...row, title: cleanedTitle };

  // Only override defaults — preserve any explicit user edit.
  if (p.deadline && row.deadline === ROW_DEFAULTS.deadline) {
    next.deadline = `${p.deadline.getFullYear()}-${String(p.deadline.getMonth() + 1).padStart(2, '0')}-${String(p.deadline.getDate()).padStart(2, '0')}`;
  }
  if (p.priority !== undefined && row.priority === ROW_DEFAULTS.priority) {
    next.priority = p.priority;
  }
  if (p.energyLevel && row.energy === ROW_DEFAULTS.energy) {
    next.energy = p.energyLevel;
  }
  if (p.estimatedMinutes !== undefined && row.estimatedMinutes === ROW_DEFAULTS.estimatedMinutes) {
    next.estimatedMinutes = p.estimatedMinutes;
  }
  return next;
}
